import { OrganizationGridItem } from "@/components/organization/organization-grid-item";
import { Input } from "@/components/ui/input";
import { NavLinkIconButton } from "@/components/ui/nav-link-icon-button";
import { Skeleton } from "@/components/ui/skeleton";
import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { useAuthGetCurrentUser } from "@/orval/auth";
import { useOrganizationGetOrganizations } from "@/orval/organization";
import { createFileRoute } from "@tanstack/react-router";
import { PlusIcon, SearchIcon } from "lucide-react";
import React from "react";

const OrganizationsGridPage: React.FC = () => {
  const [searchString, setSearchString] = React.useState<string>("");
  const searchParams = useDebounce({ searchString, pageIndex: 0, pageSize: 10 });
  const { data, isLoading, isFetching } = useOrganizationGetOrganizations(searchParams);
  const { data: currentUser } = useAuthGetCurrentUser();
  const canAddOrganization = currentUser?.email === "danevandy@hotmail.com";

  const organizations = data?.items || [];

  return (
    <div className="container min-w-0 px-4 sm:px-8">
      <h2 className="text-3xl font-semibold tracking-tight py-8 border-b text-foreground">Organizations</h2>
      <div className="mt-4 flex min-w-0 flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
        {canAddOrganization && (
          <NavLinkIconButton to="/organizations/new" icon={<PlusIcon />} className="w-full justify-center md:w-auto">
            New Organization
          </NavLinkIconButton>
        )}

        <Input
          autoFocus
          className="w-full min-w-0 md:ml-auto md:w-72"
          startAdornment={<SearchIcon size={22} className="px-1" />}
          placeholder="Search for an organization"
          value={searchString}
          onChange={(event) => setSearchString(event.target.value)}
        />
      </div>

      <div className={cn("mt-8 grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4")}>
        {isLoading ? (
          <>
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64 lg:hidden xl:block" />
          </>
        ) : (
          organizations.map((organization) => (
            <OrganizationGridItem
              key={organization.id}
              organizationListItem={organization}
              className={cn(isFetching && !isLoading && "opacity-50")}
            />
          ))
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/")({
  component: OrganizationsGridPage,
});
